import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import {
  CREDIT_ACTION_LABELS,
  CREDIT_ACTION_ORDER,
  type CreditBalance,
  formatCredits,
  type MonthCredits,
  saveCreditOverage,
  usedShare,
} from '@/lib/credits';

const TABLE = 'overflow-x-auto rounded-lg border border-domino-cardBorder bg-domino-white';
const HEAD = 'whitespace-nowrap px-3 py-2 font-medium';
const CELL = 'whitespace-nowrap px-3 py-2.5 tabular-nums';
const ROW = 'border-t border-domino-cardBorder';

const BalanceBar = ({ balance }: { balance: CreditBalance }) => {
  const share = usedShare(balance);
  const remaining = balance.granted - balance.spent;
  return (
    <div className="rounded-lg border border-domino-cardBorder bg-domino-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <span className="text-domino-ink">
          <span className="text-2xl font-semibold tabular-nums">{formatCredits(balance.spent)}</span>
          <span className="text-domino-inkSoft"> / {formatCredits(balance.granted)} crédits</span>
        </span>
        <span className="tabular-nums text-domino-inkSoft">
          {remaining >= 0 ? `${formatCredits(remaining)} restants` : `${formatCredits(-remaining)} en dépassement`}
        </span>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-domino-muted" role="progressbar"
        aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(Math.min(share, 1) * 100)}>
        <div className={`h-full rounded-full ${share >= 0.8 ? 'bg-domino-sun' : 'bg-domino-aubergine'}`}
          style={{ width: `${Math.min(share, 1) * 100}%` }} />
      </div>
      <p className="mt-2 text-xs text-domino-inkSoft">
        {balance.seats} {balance.seats > 1 ? 'sièges' : 'siège'} × {formatCredits(balance.creditsPerSeat)}
      </p>
    </div>
  );
};

const OverageSetting = ({ balance, onSaved }: { balance: CreditBalance; onSaved: () => void }) => {
  const [allowed, setAllowed] = useState(balance.overageAllowed);
  const [cap, setCap] = useState(balance.overageCap === null ? '' : String(balance.overageCap));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAllowed(balance.overageAllowed);
    setCap(balance.overageCap === null ? '' : String(balance.overageCap));
  }, [balance.overageAllowed, balance.overageCap]);

  const capValue = cap.trim() === '' ? null : Number(cap);
  const capInvalid = capValue !== null && (!Number.isInteger(capValue) || capValue <= 0);
  const changed = allowed !== balance.overageAllowed || (allowed && capValue !== balance.overageCap);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveCreditOverage(allowed, capValue);
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-domino-cardBorder bg-domino-white p-4 text-sm">
      <label className="flex items-center gap-2 text-domino-ink">
        <input type="checkbox" checked={allowed} onChange={(event) => setAllowed(event.target.checked)}
          className="h-4 w-4 accent-domino-aubergine" />
        Autoriser le dépassement
      </label>
      {allowed && (
        <label className="flex items-center gap-2 text-domino-inkSoft">
          Plafond
          <input type="number" min={1} step={1} inputMode="numeric" value={cap} placeholder="Aucun"
            onChange={(event) => setCap(event.target.value)}
            className="w-28 rounded-md border border-domino-cardBorder px-2 py-1 tabular-nums text-domino-ink" />
          crédits
        </label>
      )}
      {changed && (
        <Button size="sm" onClick={save} loading={saving} disabled={capInvalid}>Enregistrer</Button>
      )}
      {error && <p role="alert" className="w-full text-domino-inkSoft">{error}</p>}
    </div>
  );
};

/** C3: the month's credits, the overage setting and who used what. */
export const CreditsSection = ({ credits, editable, onSaved }: {
  credits: MonthCredits;
  editable: boolean;
  onSaved: () => void;
}) => {
  const actions = CREDIT_ACTION_ORDER.filter((action) => credits.people.some((person) => person.units[action]));
  return (
    <div className="space-y-4">
      <BalanceBar balance={credits.balance} />
      {editable && <OverageSetting balance={credits.balance} onSaved={onSaved} />}
      {credits.people.length > 0 && (
        <div className={TABLE}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-domino-inkSoft">
                <th className={HEAD}>Personne</th>
                {actions.map((action) => <th key={action} className={HEAD}>{CREDIT_ACTION_LABELS[action]}</th>)}
                <th className={HEAD}>Crédits</th>
              </tr>
            </thead>
            <tbody>
              {credits.people.map((person) => (
                <tr key={person.userId} className={ROW}>
                  <td className="px-3 py-2.5 font-semibold text-domino-ink">{person.name}</td>
                  {actions.map((action) => <td key={action} className={CELL}>{person.units[action] || ''}</td>)}
                  <td className={`${CELL} font-semibold`}>{formatCredits(person.credits)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
